import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import {
  API,
  authHeaders,
  alertError,
  alertSuccess,
  btnPrimary,
  fieldStyle,
  thStyle,
  tdStyle,
  pageTitleStyle,
  pageSubtitleStyle,
  ticketProductLabel,
  ticketAssigneeLabel,
  ticketClientBranchLabel,
  parseTicketListResponse,
  TICKET_LIST_PAGE_SIZE,
} from '../api/ticketsApi';
import { formatUtcToIst } from '../utils/datetime';
import TicketStatusBadge from '../components/TicketStatusBadge';
import { usePrefillTicketNo } from '../hooks/usePrefillTicketNo';
import { TicketActionBack, TicketDetailLink } from '../ticketNav';
import { useProductFilter } from '../ProductFilterContext';

const CLOSURE_TABS = [
  { id: 'mine', label: 'My Tickets' },
  { id: 'all', label: 'All Tickets' },
];

function TicketClosurePage() {
  const prefillTicket = usePrefillTicketNo();
  const { productId, selectedProduct } = useProductFilter();
  const focusRowRef = useRef(null);
  const [scope, setScope] = useState('mine');
  const [canAll, setCanAll] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [notes, setNotes] = useState({});

  const loadTickets = useCallback(async (nextScope, pageNum) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/lifecycle/completed`,
        {
          ...authHeaders(),
          params: {
            scope: nextScope,
            ...(productId ? { product_id: productId } : {}),
            limit: TICKET_LIST_PAGE_SIZE,
            skip: (pageNum - 1) * TICKET_LIST_PAGE_SIZE,
          },
        },
      );
      let { items: list, total: count } = parseTicketListResponse(res);
      if (prefillTicket && pageNum === 1) {
        list = [
          ...list.filter((t) => t.ticket_no === prefillTicket),
          ...list.filter((t) => t.ticket_no !== prefillTicket),
        ];
      }
      setTickets(list);
      setTotal(count);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to load completed tickets';
      if (nextScope === 'all' && err.response?.status === 403) {
        setCanAll(false);
        setScope('mine');
        setError('You do not have access to view all completed tickets. Showing My Tickets.');
        try {
          const mine = await axios.get(
            `${API}/lifecycle/completed`,
            {
              ...authHeaders(),
              params: {
                scope: 'mine',
                limit: TICKET_LIST_PAGE_SIZE,
                skip: 0,
              },
            },
          );
          const parsed = parseTicketListResponse(mine);
          setTickets(parsed.items);
          setTotal(parsed.total);
          setPage(1);
        } catch (e2) {
          setTickets([]);
          setTotal(0);
          setError(e2.response?.data?.detail || detail);
        }
      } else {
        setTickets([]);
        setTotal(0);
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
    } finally {
      setLoading(false);
    }
  }, [prefillTicket, productId]);

  // Probe whether All Tickets is allowed (can_close_all / manager+).
  // If opened from ticket detail with ?ticket=, prefer All when allowed so the row is visible.
  useEffect(() => {
    axios.get(`${API}/lifecycle/completed`, {
      ...authHeaders(),
      params: {
        scope: 'all',
        limit: 1,
        skip: 0,
        ...(productId ? { product_id: productId } : {}),
      },
    })
      .then(() => {
        setCanAll(true);
        if (prefillTicket) setScope('all');
      })
      .catch((err) => setCanAll(err.response?.status !== 403));
  }, [prefillTicket, productId]);

  useEffect(() => {
    setPage(1);
  }, [scope, productId]);

  useEffect(() => {
    loadTickets(scope, page);
  }, [scope, page, loadTickets]);

  useEffect(() => {
    if (!prefillTicket || loading) return;
    if (!tickets.some((t) => t.ticket_no === prefillTicket)) return;
    const t = setTimeout(() => {
      focusRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    return () => clearTimeout(t);
  }, [prefillTicket, loading, tickets]);

  const handleVerify = async (ticketNo) => {
    setVerifying(ticketNo);
    setError(null);
    setSuccess(null);
    try {
      await axios.post(
        `${API}/lifecycle/${ticketNo}/closure`,
        { verified: true, notes: notes[ticketNo] || null },
        authHeaders(),
      );
      setSuccess(`Ticket ${ticketNo} verified and closed`);
      await loadTickets(scope, page);
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(null);
    }
  };

  const visibleTabs = CLOSURE_TABS.filter((t) => t.id === 'mine' || canAll);
  const totalPages = Math.max(1, Math.ceil(total / TICKET_LIST_PAGE_SIZE));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div>
      <div className="tk-page-head">
        <div className="tk-page-head-main">
          <div className="tk-page-head-back">
            <TicketActionBack ticketNo={prefillTicket || null} />
          </div>
          <h2 style={pageTitleStyle}>Closure Verification</h2>
          <p style={pageSubtitleStyle}>
            Verify completed tickets before final closure.
            {selectedProduct ? ` Scoped to ${selectedProduct.name}.` : ''}
          </p>
        </div>
      </div>

      <div className="tk-segmented" role="tablist" aria-label="Closure scope" style={{ marginBottom: '14px' }}>
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={scope === tab.id}
            className={scope === tab.id ? 'is-active' : ''}
            onClick={() => setScope(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && <div style={alertError}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}
      {success && <div style={alertSuccess}>{success}</div>}

      {loading ? (
        <p style={{ color: '#6B7280' }}>Loading completed tickets…</p>
      ) : tickets.length === 0 ? (
        <p style={{ color: '#6B7280' }}>
          {scope === 'mine'
            ? 'No completed tickets assigned to you awaiting verification.'
            : 'No completed tickets awaiting verification.'}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                <th style={thStyle}>Ticket No</th>
                <th style={thStyle}>Product</th>
                <th style={thStyle}>Client / Branch</th>
                <th style={thStyle}>Subject</th>
                <th style={thStyle}>Assignee</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Completed (IST)</th>
                <th style={thStyle}>Notes</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => {
                const focused = prefillTicket && t.ticket_no === prefillTicket;
                return (
                  <tr
                    key={t.ticket_no}
                    ref={focused ? focusRowRef : undefined}
                    style={{
                      borderBottom: '1px solid #E5E7EB',
                      background: focused ? '#EEF0FF' : undefined,
                    }}
                  >
                    <td style={tdStyle}>
                      <TicketDetailLink ticketNo={t.ticket_no} style={{ color: '#002FA7', fontWeight: '600' }}>
                        {t.ticket_no}
                      </TicketDetailLink>
                    </td>
                    <td style={tdStyle}>{ticketProductLabel(t) || '—'}</td>
                    <td style={tdStyle}>{ticketClientBranchLabel(t)}</td>
                    <td style={tdStyle}>{t.subject}</td>
                    <td style={tdStyle}>{ticketAssigneeLabel(t)}</td>
                    <td style={tdStyle}><TicketStatusBadge status={t.status} /></td>
                    <td style={tdStyle}>{formatUtcToIst(t.updated_at || t.completed_at)}</td>
                    <td style={tdStyle}>
                      <input
                        value={notes[t.ticket_no] || ''}
                        onChange={(e) => setNotes((n) => ({ ...n, [t.ticket_no]: e.target.value }))}
                        placeholder="Verification notes"
                        style={{ ...fieldStyle, padding: '6px 8px', fontSize: '12px' }}
                        autoFocus={focused}
                      />
                    </td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        disabled={verifying === t.ticket_no}
                        onClick={() => handleVerify(t.ticket_no)}
                        style={{ ...btnPrimary, padding: '6px 12px', fontSize: '12px' }}
                      >
                        {verifying === t.ticket_no ? '…' : 'Verify & Close'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>
              Page {page} of {totalPages} · {total} total
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="tk-btn tk-btn-secondary tk-btn-sm"
                disabled={!canPrev || loading}
                onClick={() => setPage((p) => p - 1)}
              >
                <CaretLeft size={14} /> Prev
              </button>
              <button
                type="button"
                className="tk-btn tk-btn-secondary tk-btn-sm"
                disabled={!canNext || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next <CaretRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TicketClosurePage;
